/**
 * EquiposCatalogView -- Catalogo de equipos (2 niveles: modelos + unidades)
 * accesible en #/equipos. Usa NxListViewer v2 con split detail.
 *
 * - Panel izquierdo: tabla de MODELOS con conteos (total / disponibles / asignadas)
 * - Panel derecho: unidades del modelo seleccionado con su asignacion activa
 * - Formularios: crear modelo, editar modelo, crear unidad
 *
 * Backend: EquiposCatalogApiClient (mock). Endpoints reales documentados como
 * NEEDS-API en ese modulo.
 *
 * @module presentation/views/EquiposCatalogView
 */

import { renderNxList, bindNxListEvents, NX_LIST_VIEWER_VERSION } from '../shared/NxListViewer.js';
import { EquiposCatalogApiClient } from '../../infrastructure/api/EquiposCatalogApiClient.js';
import { avisoDeMaqueta, accionDeMaqueta } from '../../application/equipos/maqueta.js';
import { confirmar } from '../shared/nxDialogo.js';

// Deuda 4: este escape (no cambiaba la comilla simple) es ahora el compartido.
import { esc } from '../shared/texto.js';
import { insigniaHtml } from '../shared/insignia.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

const TYPE_LABELS = {
  impresora: 'Impresora', computador: 'Computador', tablet: 'Tablet',
  caja_registradora: 'Caja registradora', terminal_pos: 'Terminal POS',
  celular: 'Celular', router: 'Router', tv_monitor: 'TV/Monitor', otro: 'Otro',
};
const STATUS_LABELS = {
  disponible:       'Disponible',
  asignado:         'Asignado',
  en_mantenimiento: 'En mantenimiento',
  dado_de_baja:     'Dado de baja',
};
const STATUS_COLORS = {
  disponible:       'var(--accent-green)',
  asignado:         'var(--accent-blue-strong)',
  en_mantenimiento: 'var(--accent-yellow)',
  dado_de_baja:     'var(--accent-red)',
};

// Estado en memoria de la vista
let _models = [];
let _selectedModelId = null;

function fmt(dateStr) {
  if (!dateStr) return '—';
  return formatearFecha(dateStr, { day: '2-digit', month: 'short', year: 'numeric' });
}

function typeBadge(type) {
  const label = TYPE_LABELS[type] || type || '—';
  return `<span class="item-type-badge" style="background:var(--border-default);color:var(--text-heading)">${esc(label)}</span>`;
}

function countsPill(counts) {
  if (!counts) return '—';
  const c = counts;
  return `
    <span style="display:inline-flex;gap:4px;align-items:center;font-size:0.875rem">
      <span class="nx-insignia" style="padding:1px 6px;border-radius:10px;color:${STATUS_COLORS.disponible}" title="Disponibles">${c.disponibles || 0}</span>
      <span class="nx-insignia" style="padding:1px 6px;border-radius:10px;color:${STATUS_COLORS.asignado}" title="Asignadas">${c.asignadas || 0}</span>
      <span class="nx-insignia" style="padding:1px 6px;border-radius:10px;color:${STATUS_COLORS.en_mantenimiento}" title="En mantenimiento">${c.en_mantenimiento || 0}</span>
      <span style="padding:1px 6px;border-radius:10px;background:var(--border-default);color:var(--text-secondary)" title="Total">${c.total || 0}</span>
    </span>`;
}

function showToast(msg, isError = false) {
  const $t = document.getElementById('nx-equipos-toast');
  if (!$t) { console[isError ? 'error' : 'log'](msg); return; }
  $t.textContent = msg;
  $t.style.background = isError ? 'var(--accent-red)' : 'var(--accent-green)';
  $t.hidden = false;
  setTimeout(() => { $t.hidden = true; }, 2600);
}

// ---------------------------------------------------------------------------
// Render principal
// ---------------------------------------------------------------------------
export async function renderEquiposCatalog(container) {
  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Catalogo de Equipos</h1>
          <p style="font-size:0.875rem;color:var(--text-secondary);margin-top:2px">
            Modelos compartidos + unidades por serial &middot; NxListViewer v${NX_LIST_VIEWER_VERSION}
          </p>
        </div>
        <div>
          <button class="btn-primary btn-sm" id="nx-eq-new-model"${accionDeMaqueta(EquiposCatalogApiClient.ES_MAQUETA)}>+ Nuevo modelo</button>
        </div>
      </div>

      ${avisoDeMaqueta(EquiposCatalogApiClient.ES_MAQUETA)}

      <div id="nx-eq-toolbar" style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:10px">
        <select id="nx-eq-filter-type" class="form-input" style="max-width:220px">
          <option value="">Todos los tipos</option>
          ${EquiposCatalogApiClient.TYPES.map(t => `<option value="${t}">${TYPE_LABELS[t] || t}</option>`).join('')}
        </select>
      </div>

      <div id="nx-eq-model-form" hidden></div>

      <div id="nx-eq-container"></div>

      <div id="nx-equipos-toast" class="status-badge" hidden
           style="position:fixed;bottom:24px;right:24px;padding:10px 14px;color:var(--text-on-accent);z-index:1000"></div>
    </section>
  `;

  container.querySelector('#nx-eq-new-model').addEventListener('click', () => toggleModelForm());
  container.querySelector('#nx-eq-filter-type').addEventListener('change', () => renderModelsList(container));

  await loadAndRender(container);
}

async function loadAndRender(container) {
  const $c = container.querySelector('#nx-eq-container');
  $c.innerHTML = '<p class="empty-state">Cargando catalogo...</p>';

  const res = await EquiposCatalogApiClient.listModels();
  if (!res.ok) {
    $c.innerHTML = `<p class="empty-state" style="color:var(--accent-red)">Error: ${esc(res.error)}</p>`;
    return;
  }
  _models = res.data || [];
  renderModelsList(container);
}

function renderModelsList(container) {
  const $c = container.querySelector('#nx-eq-container');
  const filterType = container.querySelector('#nx-eq-filter-type').value || '';

  const data = _models
    .filter(m => !filterType || m.type === filterType)
    .map(m => ({
      ...m,
      _typeLabel: TYPE_LABELS[m.type] || m.type || '—',
      _countsHtml: countsPill(m.counts),
    }));

  const listConfig = {
    id: 'nx-eq-models-table',
    columns: [
      { key: 'name',       label: 'Modelo',      width: '30%', render: (v) => `<strong>${esc(v)}</strong>` },
      { key: 'brand',      label: 'Marca',       width: '15%', render: (v) => esc(v || '—') },
      { key: '_typeLabel', label: 'Tipo',        width: '15%', render: (v) => typeBadge(v) },
      { key: '_countsHtml',label: 'Unidades',    width: '25%', render: (v) => v },
    ],
    data,
    sortBy: 'name',
    sortDir: 'asc',
    emptyMessage: 'Sin modelos registrados',
    filterPlaceholder: 'Buscar por nombre, marca...',
  };

  $c.innerHTML = renderNxList(listConfig);
  bindNxListEvents($c, 'nx-eq-models-table', listConfig, null, (model, detailEl) => {
    _selectedModelId = model.id;
    renderModelDetail(detailEl, model);
  });
}

// ---------------------------------------------------------------------------
// Detail panel (lista de unidades del modelo seleccionado)
// ---------------------------------------------------------------------------
async function renderModelDetail(detailEl, model) {
  detailEl.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:start;gap:8px;margin-bottom:10px">
      <div>
        <h3 style="margin:0">${esc(model.name)}</h3>
        <div style="font-size:0.875rem;color:var(--text-secondary)">
          ${typeBadge(model._typeLabel || TYPE_LABELS[model.type] || model.type)}
          ${model.brand ? ` &middot; ${esc(model.brand)}` : ''}
        </div>
        ${model.description ? `<p style="font-size:0.875rem;color:var(--text-heading);margin:6px 0 0">${esc(model.description)}</p>` : ''}
      </div>
      <div style="display:flex;gap:6px;flex-wrap:wrap">
        <button class="btn-ghost btn-sm" data-eq-edit-model="${esc(model.id)}"${accionDeMaqueta(EquiposCatalogApiClient.ES_MAQUETA)}>Editar modelo</button>
        <button class="btn-ghost btn-sm btn-delete" data-eq-delete-model="${esc(model.id)}" ${accionDeMaqueta(EquiposCatalogApiClient.ES_MAQUETA, 'Eliminar modelo')}>Eliminar</button>
        <button class="btn-primary btn-sm" data-eq-new-unit="${esc(model.id)}"${accionDeMaqueta(EquiposCatalogApiClient.ES_MAQUETA)}>+ Unidad</button>
      </div>
    </div>
    <div id="nx-eq-unit-form" hidden></div>
    <div id="nx-eq-units-wrap"><p class="empty-state">Cargando unidades...</p></div>
  `;

  const res = await EquiposCatalogApiClient.listUnits(model.id);
  const $units = detailEl.querySelector('#nx-eq-units-wrap');
  if (!res.ok) {
    $units.innerHTML = `<p class="empty-state" style="color:var(--accent-red)">Error: ${esc(res.error)}</p>`;
    return;
  }
  const units = res.data || [];
  if (units.length === 0) {
    $units.innerHTML = '<p class="empty-state">Sin unidades. Agrega la primera con + Unidad.</p>';
  } else {
    $units.innerHTML = units.map(u => {
      const stColor = STATUS_COLORS[u.status] || 'var(--text-muted)';
      const stLabel = STATUS_LABELS[u.status] || u.status;
      const asg = u.currentAssignment
        ? `<div style="font-size:0.875rem;color:var(--text-secondary)">Asignada a <code>${esc(u.currentAssignment.business_id)}</code> desde ${fmt(u.currentAssignment.assigned_at)}</div>`
        : '';
      return `
        <div class="artifact-item" data-eq-unit-id="${esc(u.id)}">
          <div class="task-info">
            <div class="artifact-item-title">
              S/N: <code>${esc(u.serial)}</code>
            </div>
            <div class="artifact-item-meta">
              ${u.notes ? esc(u.notes) + ' &middot; ' : ''}
              Creada ${fmt(u.created_at)}
            </div>
            ${asg}
          </div>
          <div class="task-actions">
            ${insigniaHtml(stLabel, stColor)}
            <button class="btn-ghost btn-sm" data-eq-edit-unit="${esc(u.id)}" ${accionDeMaqueta(EquiposCatalogApiClient.ES_MAQUETA, 'Editar unidad')}>✏</button>
            <button class="btn-icon btn-delete" data-eq-delete-unit="${esc(u.id)}" ${accionDeMaqueta(EquiposCatalogApiClient.ES_MAQUETA, 'Eliminar unidad')}>&times;</button>
          </div>
        </div>`;
    }).join('');
  }

  // Delegate
  detailEl.querySelector('[data-eq-new-unit]')?.addEventListener('click', (ev) => {
    const mid = ev.currentTarget.dataset.eqNewUnit;
    toggleUnitForm(detailEl, mid);
  });
  detailEl.querySelector('[data-eq-edit-model]')?.addEventListener('click', (ev) => {
    const mid = ev.currentTarget.dataset.eqEditModel;
    const m = _models.find(x => x.id === mid);
    toggleModelForm(m);
  });
  detailEl.querySelector('[data-eq-delete-model]')?.addEventListener('click', async (ev) => {
    // El boton se guarda antes de los await: despues, ev.currentTarget es null.
    const boton = ev.currentTarget;
    const mid = boton.dataset.eqDeleteModel;
    const m = _models.find(x => x.id === mid);
    if (!await confirmar({
      titulo: `Eliminar el modelo "${m?.name || mid}"`,
      texto: 'Esta accion falla si el modelo tiene unidades registradas.',
      aceptar: 'Eliminar modelo',
      peligro: true
    })) return;
    boton.disabled = true;
    const r = await EquiposCatalogApiClient.deleteModel(mid);
    if (!r.ok) { showToast(r.error, true); boton.disabled = false; return; }
    showToast('Modelo eliminado');
    _selectedModelId = null;
    const container = document.getElementById('nx-main') || document.body;
    await loadAndRender(container);
  });
  detailEl.querySelectorAll('[data-eq-edit-unit]').forEach(btn => {
    btn.addEventListener('click', async (ev) => {
      const uid = ev.currentTarget.dataset.eqEditUnit;
      const res2 = await EquiposCatalogApiClient.getUnit(uid);
      if (!res2.ok) return showToast(res2.error, true);
      openUnitEdit(detailEl, res2.data);
    });
  });
  detailEl.querySelectorAll('[data-eq-delete-unit]').forEach(btn => {
    btn.addEventListener('click', async (ev) => {
      const uid = btn.dataset.eqDeleteUnit;
      if (!await confirmar({
        titulo: 'Eliminar esta unidad',
        texto: 'Esta accion falla si la unidad esta asignada a un negocio.',
        aceptar: 'Eliminar unidad',
        peligro: true
      })) return;
      btn.disabled = true;
      const r = await EquiposCatalogApiClient.deleteUnit(uid);
      if (!r.ok) { showToast(r.error, true); btn.disabled = false; return; }
      showToast('Unidad eliminada');
      const container = document.getElementById('nx-main') || document.body;
      await loadAndRender(container);
      // Re-abrir detalle del modelo si persiste
      const model = _models.find(m => m.id === _selectedModelId);
      const $detail = document.getElementById('nx-eq-models-table-detail');
      if ($detail && model) { $detail.hidden = false; renderModelDetail($detail, { ...model, _typeLabel: TYPE_LABELS[model.type] }); }
    });
  });
}

// ---------------------------------------------------------------------------
// Form modelo (crear / editar)
// ---------------------------------------------------------------------------
function toggleModelForm(existing = null) {
  const $wrap = document.getElementById('nx-eq-model-form');
  if (!$wrap) return;
  if (!$wrap.hidden && !existing) { $wrap.hidden = true; return; }
  $wrap.hidden = false;
  const e = existing || {};
  const typeOpts = EquiposCatalogApiClient.TYPES
    .map(t => `<option value="${t}"${e.type === t ? ' selected' : ''}>${TYPE_LABELS[t] || t}</option>`).join('');
  $wrap.innerHTML = `
    <form id="nx-eq-model-f" class="sub-edit-form" style="margin-bottom:10px">
      <div class="form-row-3">
        <div><label class="detail-label">Nombre *</label>
          <input class="form-input" name="name" value="${esc(e.name || '')}" required placeholder="Impresora Termica SAT15TUS"></div>
        <div><label class="detail-label">Marca</label>
          <input class="form-input" name="brand" value="${esc(e.brand || '')}" placeholder="SAT, Dell, HP..."></div>
        <div><label class="detail-label">Tipo *</label>
          <select class="form-input" name="type" required>
            <option value="">Seleccionar...</option>${typeOpts}
          </select></div>
      </div>
      <div><label class="detail-label">Descripcion</label>
        <input class="form-input" name="description" value="${esc(e.description || '')}" placeholder="Notas del modelo"></div>
      <div class="form-actions-row">
        <button type="submit" class="btn-primary btn-sm">${existing ? 'Guardar cambios' : 'Crear modelo'}</button>
        <button type="button" class="btn-ghost btn-sm" id="nx-eq-model-cancel">Cancelar</button>
        <span class="sub-edit-err auth-error" hidden></span>
      </div>
    </form>`;

  document.getElementById('nx-eq-model-cancel').addEventListener('click', () => { $wrap.hidden = true; });
  document.getElementById('nx-eq-model-f').addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const fd = new FormData(ev.target);
    const payload = {
      name: fd.get('name'),
      brand: fd.get('brand') || null,
      type: fd.get('type'),
      description: fd.get('description') || null,
    };
    const $btn = ev.target.querySelector('[type=submit]');
    const $err = ev.target.querySelector('.sub-edit-err');
    $btn.disabled = true;
    const res = existing
      ? await EquiposCatalogApiClient.updateModel(existing.id, payload)
      : await EquiposCatalogApiClient.createModel(payload);
    if (res.ok) {
      showToast(existing ? 'Modelo actualizado' : 'Modelo creado');
      $wrap.hidden = true;
      const container = document.getElementById('nx-main') || document.body;
      await loadAndRender(container);
    } else {
      $err.textContent = res.error; $err.hidden = false; $btn.disabled = false;
    }
  });
}

// ---------------------------------------------------------------------------
// Form unidad (crear)
// ---------------------------------------------------------------------------
function toggleUnitForm(detailEl, modelId) {
  const $wrap = detailEl.querySelector('#nx-eq-unit-form');
  if (!$wrap) return;
  if (!$wrap.hidden) { $wrap.hidden = true; return; }
  $wrap.hidden = false;
  const statusOpts = EquiposCatalogApiClient.UNIT_STATUSES
    .map(s => `<option value="${s}"${s === 'disponible' ? ' selected' : ''}>${STATUS_LABELS[s]}</option>`).join('');
  $wrap.innerHTML = `
    <form id="nx-eq-unit-f" class="sub-edit-form" style="margin-bottom:10px">
      <div class="form-row-3">
        <div><label class="detail-label">Serial *</label>
          <input class="form-input" name="serial" required placeholder="SAT-0001"></div>
        <div><label class="detail-label">Estado inicial</label>
          <select class="form-input" name="status">${statusOpts}</select></div>
        <div><label class="detail-label">Notas</label>
          <input class="form-input" name="notes" placeholder="Observaciones"></div>
      </div>
      <div class="form-actions-row">
        <button type="submit" class="btn-primary btn-sm">Crear unidad</button>
        <button type="button" class="btn-ghost btn-sm" id="nx-eq-unit-cancel">Cancelar</button>
        <span class="sub-edit-err auth-error" hidden></span>
      </div>
    </form>`;
  $wrap.querySelector('#nx-eq-unit-cancel').addEventListener('click', () => { $wrap.hidden = true; });
  $wrap.querySelector('#nx-eq-unit-f').addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const fd = new FormData(ev.target);
    const $btn = ev.target.querySelector('[type=submit]');
    const $err = ev.target.querySelector('.sub-edit-err');
    $btn.disabled = true;
    const res = await EquiposCatalogApiClient.createUnit(modelId, {
      serial: fd.get('serial'),
      status: fd.get('status'),
      notes:  fd.get('notes') || null,
    });
    if (res.ok) {
      showToast('Unidad creada');
      const container = document.getElementById('nx-main') || document.body;
      await loadAndRender(container);
      // Volver a abrir detalle del modelo actual
      const model = _models.find(m => m.id === modelId);
      const $detail = document.getElementById('nx-eq-models-table-detail');
      if ($detail && model) { $detail.hidden = false; renderModelDetail($detail, { ...model, _typeLabel: TYPE_LABELS[model.type] }); }
    } else {
      $err.textContent = res.error; $err.hidden = false; $btn.disabled = false;
    }
  });
}

function openUnitEdit(detailEl, unit) {
  const $wrap = detailEl.querySelector('#nx-eq-unit-form');
  if (!$wrap) return;
  $wrap.hidden = false;
  const statusOpts = EquiposCatalogApiClient.UNIT_STATUSES
    .map(s => `<option value="${s}"${unit.status === s ? ' selected' : ''}>${STATUS_LABELS[s]}</option>`).join('');
  $wrap.innerHTML = `
    <form id="nx-eq-unit-edit-f" class="sub-edit-form" style="margin-bottom:10px">
      <div class="form-row-3">
        <div><label class="detail-label">Serial *</label>
          <input class="form-input" name="serial" value="${esc(unit.serial)}" required></div>
        <div><label class="detail-label">Estado</label>
          <select class="form-input" name="status">${statusOpts}</select></div>
        <div><label class="detail-label">Notas</label>
          <input class="form-input" name="notes" value="${esc(unit.notes || '')}"></div>
      </div>
      <div class="form-actions-row">
        <button type="submit" class="btn-primary btn-sm">Guardar</button>
        <button type="button" class="btn-ghost btn-sm" id="nx-eq-unit-edit-cancel">Cancelar</button>
        <span class="sub-edit-err auth-error" hidden></span>
      </div>
    </form>`;
  $wrap.querySelector('#nx-eq-unit-edit-cancel').addEventListener('click', () => { $wrap.hidden = true; });
  $wrap.querySelector('#nx-eq-unit-edit-f').addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const fd = new FormData(ev.target);
    const $btn = ev.target.querySelector('[type=submit]');
    const $err = ev.target.querySelector('.sub-edit-err');
    $btn.disabled = true;
    const res = await EquiposCatalogApiClient.updateUnit(unit.id, {
      serial: fd.get('serial'),
      status: fd.get('status'),
      notes:  fd.get('notes') || null,
    });
    if (res.ok) {
      showToast('Unidad actualizada');
      const container = document.getElementById('nx-main') || document.body;
      await loadAndRender(container);
      const model = _models.find(m => m.id === unit.model_id);
      const $detail = document.getElementById('nx-eq-models-table-detail');
      if ($detail && model) { $detail.hidden = false; renderModelDetail($detail, { ...model, _typeLabel: TYPE_LABELS[model.type] }); }
    } else {
      $err.textContent = res.error; $err.hidden = false; $btn.disabled = false;
    }
  });
}
